import { stripLocalePrefix } from './locale-path';

/**
 * Catalog-group partition (ADR-0195).
 *
 * The per-locale message catalogs are monolithic JSONs (~456 KB EN — 56% of it
 * `docs.*`), and hydration awaits the active catalog, so every visitor paid the
 * whole site's strings on the critical path. The catalogs are split at build
 * time (see `vite-plugin-catalog-groups.ts`) into three chunks per locale:
 *
 *   - `core`  — landing page + shared chrome (header/footer/consent/SEO/404) —
 *               the only group the home route needs (~10% of the catalog).
 *   - `pages` — every lazy non-docs route (pricing, trust, legal, vs, …).
 *   - `docs`  — the `docs.*` namespace (the 56%), loaded only on `/docs` routes.
 *
 * `groupOfKey` is the single classifier both the build-time splitter and the
 * coverage tests share; an unknown new namespace lands in `core` (always
 * loaded), so a new section can never render raw keys because of this split.
 * The route→group map below mirrors `routes.tsx`'s route classes and is proven
 * sufficient per prerendered route by `catalog-route-coverage.test.tsx`.
 */

export const CATALOG_GROUPS = ['core', 'pages', 'docs'] as const;
export type CatalogGroup = (typeof CATALOG_GROUPS)[number];

const DOCS_PREFIX = 'docs.';

/** Namespaces rendered only by lazy non-docs routes (see `routes.tsx`). */
const PAGES_PREFIXES: readonly string[] = [
  'a11ystmt.',
  'about.',
  'ai.',
  'benchmarks.',
  'compliance.',
  'contact.',
  'cookies.',
  'disclosure.',
  'dpa.',
  'features.',
  'howItWorks.',
  'onepagers.',
  'pricing.',
  'privacy.',
  'questionnaire.',
  'refunds.',
  'security.',
  'status.',
  'subprocessors.',
  'terms.',
  'trusthub.',
  'vs.',
];

/** Classify a catalog key into the group chunk that carries it. */
export function groupOfKey(key: string): CatalogGroup {
  if (key.startsWith(DOCS_PREFIX)) return 'docs';
  if (PAGES_PREFIXES.some((prefix) => key.startsWith(prefix))) return 'pages';
  return 'core';
}

/**
 * The catalog groups a document at `pathname` needs before it can hydrate.
 * Mirrors the route classes in `routes.tsx` (`preloadForPath`): home is the
 * eager landing page (core-only), `/docs` trees add `docs`, every other route
 * is a lazy page chunk that adds `pages`. Unknown paths render the 404 (core
 * keys) — they get `pages` too, which errs on the loaded side.
 */
export function groupsForPath(pathname: string): readonly CatalogGroup[] {
  const enPath = stripLocalePrefix(pathname);
  const normalized = enPath.replace(/\/+$/, '') || '/';
  if (normalized === '/') return ['core'];
  if (/^\/docs(\/|$)/.test(normalized)) return ['core', 'docs'];
  return ['core', 'pages'];
}
